import { useState } from 'react';
import { ChevronDown, ChevronUp, Trash2, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { ActionEntry } from '@/hooks/useActionLog';

interface ActionLogPanelProps {
  entries: ActionEntry[];
  onClear: () => void;
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function ActionLogPanel({ entries, onClear }: ActionLogPanelProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={cn(
        'absolute bottom-8 right-2 z-30 bg-background/95 backdrop-blur border border-border rounded-lg shadow-lg transition-all duration-200',
        expanded ? 'w-72' : 'w-auto'
      )}
      data-testid="action-log-panel"
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 px-3 py-1.5 w-full hover:bg-muted/50 rounded-t-lg transition-colors"
      >
        <Activity className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">
          Activity
          {entries.length > 0 && !expanded && (
            <span className="ml-1 text-[10px] text-primary">({entries.length})</span>
          )}
        </span>
        {expanded ? (
          <ChevronDown className="w-3 h-3 ml-auto text-muted-foreground" />
        ) : (
          <ChevronUp className="w-3 h-3 ml-auto text-muted-foreground" />
        )}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-border">
          {entries.length > 0 && (
            <div className="flex justify-end px-2 pt-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-[10px] text-muted-foreground hover:text-destructive"
                onClick={onClear}
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Clear
              </Button>
            </div>
          )}
          <ScrollArea className="h-48">
            {entries.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">No actions yet</p>
            ) : (
              <div className="px-2 pb-2 space-y-0.5">
                {entries.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-muted/30 transition-colors"
                  >
                    <span className="text-[10px] text-muted-foreground tabular-nums flex-shrink-0 mt-0.5">
                      {formatTime(entry.timestamp)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <span className="text-xs font-medium block truncate">{entry.action}</span>
                      {entry.detail && (
                        <span className="text-[10px] text-muted-foreground block truncate">{entry.detail}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
